document.addEventListener('alpine:init', () => {
  Alpine.data('subscriptionProductBuilder', () => ({
    items: {},
    variants: [],

    /*
     * Subscription only.
     *
     * Keep purchaseMode for compatibility with
     * existing Liquid blocks such as the
     * subscription frequency selector.
     */
    purchaseMode: 'subscription',

    availableFrequencies: [],
    selectedFrequency: null,

    currency: 'GBP',

    isAddingToCart: false,
    addToCartError: '',

    showFloatingAtc: false,
    floatingAtcObserver: null,

    init() {
      this.loadProductData();
      this.buildAvailableFrequencies();

      if (this.availableFrequencies.length) {
        this.selectedFrequency =
          this.availableFrequencies[0].key;
      }

      this.$nextTick(() => {
        this.initFloatingAtc();
      });
    },

    destroy() {
      if (this.floatingAtcObserver) {
        this.floatingAtcObserver.disconnect();
        this.floatingAtcObserver = null;
      }
    },

    /*
     * ----------------------------------------
     * Subscription compatibility
     * ----------------------------------------
     */

    get isSubscription() {
      return true;
    },

    get isOneTime() {
      return false;
    },

    /*
     * ----------------------------------------
     * Selected items
     * ----------------------------------------
     */

    get totalQuantity() {
      return Object.values(this.items).reduce(
        (total, item) => {
          return total + item.quantity;
        },
        0
      );
    },

    get selectedItems() {
      return Object.entries(this.items).filter(
        ([, item]) => item.quantity > 0
      );
    },

    get hasSelectedItems() {
      return this.totalQuantity > 0;
    },

    getQuantity(variantId) {
      return this.items[variantId]?.quantity || 0;
    },

    add(variantId) {
      const variant = this.getVariant(variantId);

      if (!variant || !variant.available) return;

      if (!this.items[variantId]) {
        this.items[variantId] = {
          quantity: 1
        };

        return;
      }

      this.items[variantId].quantity += 1;
    },

    increment(variantId) {
      this.add(variantId);
    },

    decrement(variantId) {
      if (!this.items[variantId]) return;

      this.items[variantId].quantity -= 1;

      if (this.items[variantId].quantity <= 0) {
        delete this.items[variantId];
      }
    },

    /*
     * ----------------------------------------
     * Variant helpers
     * ----------------------------------------
     */

    getVariant(variantId) {
      return (
        this.variants.find(
          (variant) =>
            Number(variant.id) === Number(variantId)
        ) || null
      );
    },

    /*
     * ----------------------------------------
     * Subscription frequency
     * ----------------------------------------
     */

    setFrequency(frequency) {
      const exists = this.availableFrequencies.some(
        (item) => item.key === frequency
      );

      if (!exists) return;

      this.selectedFrequency = frequency;
      this.addToCartError = '';
    },

    normalizeFrequency(name) {
      if (!name) return '';

      return String(name)
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
    },

    buildAvailableFrequencies() {
      this.availableFrequencies = [];

      if (!this.variants.length) return;

      const variantsWithPlans = this.variants.filter(
        (variant) =>
          Array.isArray(variant.sellingPlans) &&
          variant.sellingPlans.length
      );

      if (!variantsWithPlans.length) return;

      const frequencyMaps = variantsWithPlans.map(
        (variant) => {
          const frequencies = new Map();

          variant.sellingPlans.forEach((plan) => {
            const key = this.normalizeFrequency(plan.name);

            if (!key) return;

            if (!frequencies.has(key)) {
              frequencies.set(key, {
                key,
                label: plan.name
              });
            }
          });

          return frequencies;
        }
      );

      const firstMap = frequencyMaps[0];

      this.availableFrequencies = Array
        .from(firstMap.values())
        .filter((frequency) =>
          frequencyMaps.every((map) =>
            map.has(frequency.key)
          )
        );
    },

    getSellingPlanForFrequency(
      variantId,
      frequencyKey = this.selectedFrequency
    ) {
      const variant = this.getVariant(variantId);

      if (
        !variant ||
        !frequencyKey ||
        !Array.isArray(variant.sellingPlans)
      ) {
        return null;
      }

      return (
        variant.sellingPlans.find(
          (plan) =>
            this.normalizeFrequency(plan.name) ===
            frequencyKey
        ) || null
      );
    },

    getSellingPlanId(variantId) {
      return (
        this.getSellingPlanForFrequency(variantId)?.id ||
        null
      );
    },

    /*
     * ----------------------------------------
     * Pricing
     * ----------------------------------------
     */

    getOneTimePrice(variantId) {
      const variant = this.getVariant(variantId);

      return variant?.price ?? 0;
    },

    getSubscriptionPrice(variantId) {
      const plan =
        this.getSellingPlanForFrequency(variantId);

      return plan?.price ?? null;
    },

    getItemPrice(variantId) {
      return (
        this.getSubscriptionPrice(variantId) ??
        this.getOneTimePrice(variantId)
      );
    },

    get subtotal() {
      return this.selectedItems.reduce(
        (total, [variantId, item]) => {
          return (
            total +
            this.getItemPrice(variantId) *
              item.quantity
          );
        },
        0
      );
    },

    get oneTimeSubtotal() {
      return this.selectedItems.reduce(
        (total, [variantId, item]) => {
          return (
            total +
            this.getOneTimePrice(variantId) *
              item.quantity
          );
        },
        0
      );
    },

    /*
     * Retained for compatibility with
     * summary/floating ATC markup.
     */
    get subscriptionSubtotal() {
      return this.subtotal;
    },

    get savingsAmount() {
      return Math.max(
        0,
        this.oneTimeSubtotal - this.subtotal
      );
    },

    get savingsPercentage() {
      if (
        this.oneTimeSubtotal <= 0 ||
        this.savingsAmount <= 0
      ) {
        return 0;
      }

      return Math.round(
        (
          this.savingsAmount /
          this.oneTimeSubtotal
        ) * 100
      );
    },

    formatMoney(cents) {
      const value = Number(cents);

      if (!Number.isFinite(value)) return '';

      try {
        return new Intl.NumberFormat(
          document.documentElement.lang || 'en-GB',
          {
            style: 'currency',
            currency: this.currency
          }
        ).format(value / 100);
      } catch (error) {
        return `£${(value / 100).toFixed(2)}`;
      }
    },

    /*
     * ----------------------------------------
     * Floating CTA
     * ----------------------------------------
     */

    initFloatingAtc() {
      const mainAtc = this.$root.querySelector(
        '[data-subscription-main-atc]'
      );

      if (!mainAtc) {
        this.showFloatingAtc = false;
        return;
      }

      if (this.floatingAtcObserver) {
        this.floatingAtcObserver.disconnect();
      }

      this.floatingAtcObserver =
        new IntersectionObserver(
          ([entry]) => {
            this.showFloatingAtc =
              !entry.isIntersecting;
          },
          {
            threshold: 0.1
          }
        );

      this.floatingAtcObserver.observe(mainAtc);
    },

    /*
     * ----------------------------------------
     * Reached free gifts
     * ----------------------------------------
     */

    getReachedGiftItems() {
      const giftItems = [];
      const addedVariantIds = new Set();

      const tiers = this.$root.querySelectorAll(
        '[data-summary-tier]'
      );

      tiers.forEach((tier) => {
        const requiredQuantity =
          Number(tier.dataset.tierQuantity) || 0;

        const giftVariantId =
          Number(tier.dataset.giftVariantId) || 0;

        if (
          requiredQuantity <= 0 ||
          giftVariantId <= 0 ||
          this.totalQuantity < requiredQuantity
        ) {
          return;
        }

        /*
         * Prevent accidental duplicate gifts
         * when multiple tiers use the same variant.
         */
        if (addedVariantIds.has(giftVariantId)) {
          return;
        }

        addedVariantIds.add(giftVariantId);

        giftItems.push({
          id: giftVariantId,
          quantity: 1,
          properties: {
            _subscription_builder_gift: 'true',
            _subscription_builder_tier:
              String(requiredQuantity)
          }
        });
      });

      return giftItems;
    },

    /*
     * ----------------------------------------
     * Cart payload
     * ----------------------------------------
     */

    buildCartItems() {
      const subscriptionItems =
        this.selectedItems
          .map(([variantId, item]) => {
            const variant =
              this.getVariant(variantId);

            if (
              !variant ||
              !variant.available ||
              item.quantity <= 0
            ) {
              return null;
            }

            const sellingPlanId =
              this.getSellingPlanId(variantId);

            if (!sellingPlanId) {
              return null;
            }

            return {
              id: Number(variantId),
              quantity: Number(item.quantity),
              selling_plan: Number(sellingPlanId)
            };
          })
          .filter(Boolean);

      const giftItems =
        this.getReachedGiftItems();

      return [
        ...subscriptionItems,
        ...giftItems
      ];
    },

    validateCart() {
      this.addToCartError = '';

      if (!this.hasSelectedItems) {
        this.addToCartError =
          'Please select at least one product.';

        return false;
      }

      if (!this.selectedFrequency) {
        this.addToCartError =
          'Please select a delivery frequency.';

        return false;
      }

      const missingPlan =
        this.selectedItems.some(([variantId]) => {
          return !this.getSellingPlanId(variantId);
        });

      if (missingPlan) {
        this.addToCartError =
          'One or more selected products are not available for this delivery frequency.';

        return false;
      }

      return true;
    },

    /*
     * ----------------------------------------
     * Add subscription + gifts then checkout
     * ----------------------------------------
     */

    async addToCart() {
      if (this.isAddingToCart) return;

      if (!this.validateCart()) return;

      const items = this.buildCartItems();

      if (!items.length) {
        this.addToCartError =
          'Unable to add the selected products.';

        return;
      }

      this.isAddingToCart = true;
      this.addToCartError = '';

      try {
        const root =
          window.Shopify?.routes?.root || '/';

        const response = await fetch(
          `${root}cart/add.js`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            },
            body: JSON.stringify({
              items
            })
          }
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(
            data?.description ||
            data?.message ||
            'Unable to add products to cart.'
          );
        }

        window.location.assign(
          `${root}checkout`
        );

      } catch (error) {
        console.error(
          'Subscription builder checkout error:',
          error
        );

        this.addToCartError =
          error?.message ||
          'Something went wrong. Please try again.';

        this.isAddingToCart = false;
      }
    },

    /*
     * ----------------------------------------
     * Product JSON
     * ----------------------------------------
     */

    loadProductData() {
      const script = this.$root.querySelector(
        '[data-subscription-builder-data]'
      );

      if (!script) {
        console.warn(
          'Subscription builder data not found.'
        );

        return;
      }

      try {
        const data = JSON.parse(
          script.textContent
        );

        this.variants = Array.isArray(data.variants)
          ? data.variants
          : [];

        if (data.currency) {
          this.currency = data.currency;
        }

      } catch (error) {
        console.error(
          'Unable to parse subscription builder data:',
          error
        );

        this.variants = [];
      }
    }
  }));
});